import { proxy } from 'valtio/vanilla'

/**
 * Represents a game in the arcade
 */
export interface Game {
  id: string
  name: string
  /** Game version (semver format, e.g., "1.0.0") */
  version: string
  thumbnail?: string
  /** Function that initializes and runs the game in the container */
  load?: (container: HTMLElement) => void | (() => void)
  /** Cleanup function returned by load, called when exiting the game */
  cleanup?: () => void
}

/**
 * Game state managed by valtio
 */
export interface GameState {
  /** List of available games */
  games: Game[]
  /** Index of the currently selected game in the carousel */
  selectedIndex: number
  /** The game currently being played (null if none) */
  activeGame: Game | null
  /** Whether a game is currently loading */
  isLoading: boolean
}

/**
 * Creates the initial game state with placeholder games
 */
function createInitialGameState(): GameState {
  return {
    games: [
      { id: 'placeholder-1', name: 'Coming Soon', version: '0.0.0' },
      { id: 'placeholder-2', name: 'Coming Soon', version: '0.0.0' },
      { id: 'placeholder-3', name: 'Coming Soon', version: '0.0.0' },
    ],
    selectedIndex: 0,
    activeGame: null,
    isLoading: false,
  }
}

/**
 * The game store
 */
export const gameStore = proxy<GameState>(createInitialGameState())

/**
 * Select the next game in the carousel
 */
export function selectNext(): void {
  if (gameStore.games.length === 0) return
  gameStore.selectedIndex = (gameStore.selectedIndex + 1) % gameStore.games.length
}

/**
 * Select the previous game in the carousel
 */
export function selectPrevious(): void {
  if (gameStore.games.length === 0) return
  gameStore.selectedIndex =
    (gameStore.selectedIndex - 1 + gameStore.games.length) % gameStore.games.length
}

/**
 * Select a game by its index
 */
export function selectGame(index: number): void {
  if (index >= 0 && index < gameStore.games.length) {
    gameStore.selectedIndex = index
  }
}

/**
 * Get the currently selected game
 */
export function getSelectedGame(): Game | undefined {
  return gameStore.games[gameStore.selectedIndex]
}

/**
 * Launch the currently selected game
 */
export function launchGame(container: HTMLElement): void {
  const game = getSelectedGame()
  if (!game) return

  // Exit any currently running game
  if (gameStore.activeGame) {
    exitGame()
  }

  gameStore.isLoading = true
  gameStore.activeGame = game

  if (game.load) {
    const cleanup = game.load(container)
    if (cleanup) {
      game.cleanup = cleanup
    }
  }

  gameStore.isLoading = false
}

/**
 * Exit the currently active game
 */
export function exitGame(): void {
  if (!gameStore.activeGame) return

  if (gameStore.activeGame.cleanup) {
    gameStore.activeGame.cleanup()
  }

  gameStore.activeGame = null
}

/**
 * Add a new game to the arcade
 */
export function addGame(game: Game): void {
  // Remove placeholder games when adding real games
  if (gameStore.games.every((g) => g.id.startsWith('placeholder'))) {
    gameStore.games = []
  }
  gameStore.games.push(game)
}

/**
 * Reset the game store to initial state (useful for testing)
 */
export function resetGameStore(): void {
  const initial = createInitialGameState()
  gameStore.games = initial.games
  gameStore.selectedIndex = initial.selectedIndex
  gameStore.activeGame = initial.activeGame
  gameStore.isLoading = initial.isLoading
}
